// Pending receipts only. Never a student snapshot, credential, or guest store.
export const OUTBOX_PREFIX = 'tmag_student_progress_outbox_v1:';
export const MAX_PENDING_ATTEMPTS = 100; // Per student, across that student's lineages on this browser.
export const MAX_PENDING_BYTES = 16384;
const studentPrefix = studentId => `${OUTBOX_PREFIX}${studentId}:`;
const keyFor = (studentId, context, id) => `${studentPrefix(studentId)}${context}:${id}`;

export class OutboxStorageError extends Error {}

function storage(operation) {
  try { return operation(); }
  catch { throw new OutboxStorageError("Pending progress storage unavailable"); }
}
function keys() {
  return storage(() => {
  const result = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(OUTBOX_PREFIX)) result.push(key);
  }
  return result;
  });
}
function decode(key) {
  const value = storage(() => localStorage.getItem(key));
  try {
    const record = JSON.parse(value);
    if (record?.version !== 1 || typeof record.studentId !== 'string' || !/^[a-f0-9]{64}$/.test(record.context) ||
        typeof record.attempt?.client_session_id !== 'string' || !/^[a-f0-9-]{36}$/i.test(record.attempt.client_session_id) || typeof record.attempt.completed_at !== 'string' || !Number.isFinite(Date.parse(record.attempt.completed_at)) || key !== keyFor(record.studentId, record.context, record.attempt.client_session_id) ||
        !['pending', 'blocked'].includes(record.state)) return null;
    return record;
  } catch { return null; }
}
export function ensureOutboxWritable() {
  // A fixed, separate probe is removed synchronously; it holds no user data.
  const probe = 'tmag_student_progress_outbox_probe_v1';
  try { localStorage.setItem(probe, '1'); localStorage.removeItem(probe); }
  catch { throw new OutboxStorageError('Pending progress storage unavailable'); }
}
export function hasStudentOutbox(studentId) { return keys().some(key => key.startsWith(studentPrefix(studentId))); }
export function outboxHasRoom(studentId) { return keys().filter(key => key.startsWith(studentPrefix(studentId))).length < MAX_PENDING_ATTEMPTS; }
export function restorePending(studentId, context, { recoverPrevious = false } = {}) {
  const compatible = [], obsolete = [], previous = [], invalid = [];
  for (const key of keys()) {
    if (!key.startsWith(studentPrefix(studentId))) continue; // Never decode another child's events.
    const record = decode(key);
    if (record && record.context === context) compatible.push(record);
    else if (!recoverPrevious) obsolete.push(key); // Preserve the OFF contract.
    else if (record) previous.push(record);
    else invalid.push(key); // Retained for review, never submitted or deleted.
  }
  compatible.sort((a,b) => (a.order || 0) - (b.order || 0) || a.attempt.completed_at.localeCompare(b.attempt.completed_at) || (a.attempt.mission_index || 0) - (b.attempt.mission_index || 0) || a.attempt.client_session_id.localeCompare(b.attempt.client_session_id));
  if (!recoverPrevious) return { compatible, obsolete };
  // Preserve capture order within each lineage; older lineages precede the active one.
  const groups = new Map();
  for (const record of previous) {
    if (!groups.has(record.context)) groups.set(record.context, []);
    groups.get(record.context).push(record);
  }
  const ordered = [...groups.values()].sort((a,b) =>
    a.map(r => r.attempt.completed_at).sort()[0].localeCompare(b.map(r => r.attempt.completed_at).sort()[0]));
  for (const group of ordered) group.sort((a,b) => (a.order || 0) - (b.order || 0) || a.attempt.completed_at.localeCompare(b.attempt.completed_at) || (a.attempt.mission_index || 0) - (b.attempt.mission_index || 0) || a.attempt.client_session_id.localeCompare(b.attempt.client_session_id));
  return { compatible, obsolete, previous: ordered.flat(), invalid };
}
export function persistPending(studentId, context, attempt) {
  const key = keyFor(studentId, context, attempt.client_session_id);
  const existing = storage(() => localStorage.getItem(key));
  if (existing !== null) {
    const record = decode(key);
    if (!record || JSON.stringify(record.attempt) !== JSON.stringify(attempt)) throw new OutboxStorageError('Pending attempt conflict');
    return record;
  }
  // Capture order is separate from event timestamps/UUIDs; exact retry never changes it.
  const order = 1 + Math.max(0, ...restorePending(studentId, context).compatible.map(record => record.order || 0));
  const record = { version: 1, studentId, context, attempt, state: 'pending', order };
  const serialized = JSON.stringify(record);
  if (serialized.length > MAX_PENDING_BYTES || !outboxHasRoom(studentId)) throw new OutboxStorageError('Pending progress storage full');
  try { localStorage.setItem(key, serialized); }
  catch { throw new OutboxStorageError('Pending progress storage unavailable'); }
  // One key per UUID avoids read-modify-write loss between tabs. setItem is synchronous.
  return record;
}
export function acknowledgePending(studentId, context, attempt) {
  const key = keyFor(studentId, context, attempt.client_session_id), record = decode(key);
  // An ACK deletes only the exact event sent, never a whole child partition.
  if (record && JSON.stringify(record.attempt) === JSON.stringify(attempt)) storage(() => localStorage.removeItem(key));
}
export function markPendingConflict(studentId, context, attempt) {
  const key = keyFor(studentId, context, attempt.client_session_id), record = decode(key);
  if (record && JSON.stringify(record.attempt) === JSON.stringify(attempt)) {
    storage(() => localStorage.setItem(key, JSON.stringify({ ...record, state: 'blocked' })));
  }
}
export function discardConflicts(studentId, context) {
  for (const record of restorePending(studentId, context).compatible) {
    if (record.state === 'blocked') storage(() => localStorage.removeItem(keyFor(studentId, context, record.attempt.client_session_id)));
  }
}
export function discardObsolete(studentId, context) {
  // Called only after explicit confirmation, under this student's authenticated gate.
  for (const key of restorePending(studentId, context).obsolete) storage(() => localStorage.removeItem(key));
}

// Inspect the exact original slot after asynchronous verification. A different
// tab may have acknowledged it; never recreate that slot or copy it elsewhere.
export function readPending(studentId, context, attempt) {
  const key = keyFor(studentId, context, attempt.client_session_id);
  if (storage(() => localStorage.getItem(key)) === null) return null;
  const record = decode(key);
  if (!record || JSON.stringify(record.attempt) !== JSON.stringify(attempt)) {
    throw new OutboxStorageError('Pending attempt conflict');
  }
  return record;
}
